import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const SetLogEntry = ({
  exercise,
  currentSetNumber,
  previousSet,
  onLogSet,
  loading
}) => {
  const [weight, setWeight] = useState(previousSet?.weight_used || exercise?.target_weight || 0);
  const [reps, setReps] = useState(previousSet?.reps_completed || exercise?.target_reps || 0);
  const [restDuration, setRestDuration] = useState(60);
  const [difficultyRating, setDifficultyRating] = useState(5);
  const [notes, setNotes] = useState('');
  const [showAdvanced, setShowAdvanced] = useState(false);

  const handleSubmit = () => {
    onLogSet({
      weight,
      reps,
      restDuration,
      difficultyRating,
      notes
    });

    setNotes('');
  };

  const quickAdjustWeight = (amount) => {
    setWeight(prev => Math.max(0, prev + amount));
  };

  const quickAdjustReps = (amount) => {
    setReps(prev => Math.max(0, prev + amount));
  };

  return (
    <div className="bg-card border border-border rounded-lg shadow-md p-4 md:p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg md:text-xl font-heading font-semibold text-foreground">
          Log Set {currentSetNumber}
        </h3>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setShowAdvanced(!showAdvanced)}
          iconName={showAdvanced ? 'ChevronUp' : 'ChevronDown'}
        >
          {showAdvanced ? 'Less' : 'More'}
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            Weight (lbs)
          </label>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="icon"
              onClick={() => quickAdjustWeight(-5)}
              aria-label="Decrease weight by 5 lbs"
            >
              <Icon name="Minus" size={16} />
            </Button>
            <Input
              type="number"
              value={weight}
              onChange={(e) => setWeight(Number(e?.target?.value))}
              className="text-center data-text"
              min="0"
              step="5"
            />
            <Button
              variant="outline"
              size="icon"
              onClick={() => quickAdjustWeight(5)}
              aria-label="Increase weight by 5 lbs"
            >
              <Icon name="Plus" size={16} />
            </Button>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            Reps
          </label>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="icon"
              onClick={() => quickAdjustReps(-1)}
              aria-label="Decrease reps by 1"
            >
              <Icon name="Minus" size={16} />
            </Button>
            <Input
              type="number"
              value={reps}
              onChange={(e) => setReps(Number(e?.target?.value))}
              className="text-center data-text"
              min="0"
            />
            <Button
              variant="outline"
              size="icon"
              onClick={() => quickAdjustReps(1)}
              aria-label="Increase reps by 1"
            >
              <Icon name="Plus" size={16} />
            </Button>
          </div>
        </div>
      </div>

      {showAdvanced && (
        <div className="space-y-4 mb-4 p-4 bg-muted rounded-lg">
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Rest Duration (seconds)
            </label>
            <Input
              type="number"
              value={restDuration}
              onChange={(e) => setRestDuration(Number(e?.target?.value))}
              min="0"
              step="15"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Difficulty Rating (1-10)
            </label>
            <div className="flex items-center gap-2">
              <Input
                type="range"
                value={difficultyRating}
                onChange={(e) => setDifficultyRating(Number(e?.target?.value))}
                min="1"
                max="10"
                className="flex-1"
              />
              <span className="text-lg font-bold data-text text-foreground w-8 text-center">
                {difficultyRating}
              </span>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Notes (optional)
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e?.target?.value)}
              placeholder="Form notes, how you felt, etc."
              className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              rows={2}
            />
          </div>
        </div>
      )}

      <Button
        onClick={handleSubmit}
        className="w-full"
        size="lg"
        iconName="Check"
        disabled={loading}
      >
        {loading ? 'Logging...' : 'Log Set'}
      </Button>

      {previousSet && (
        <div className="mt-4 p-3 bg-muted rounded-lg">
          <p className="text-xs caption text-muted-foreground mb-1">
            Previous Set
          </p>
          <p className="text-sm text-foreground">
            {previousSet?.weight_used} lbs × {previousSet?.reps_completed} reps
            {previousSet?.difficulty_rating && (
              <span className="text-muted-foreground ml-2">
                (Difficulty: {previousSet?.difficulty_rating}/10)
              </span>
            )}
          </p>
        </div>
      )}
    </div>
  );
};

export default SetLogEntry;